import React, { useEffect, useState } from "react"

import { gql, useQuery } from "urql"

import WithLoadingIndicator from "~tailwindui/Basics/WithLoadingIndicator"

import ordered from "src/ordered"

import useClassPeriodRefresh from "components/AudioRoom/useClassPeriodRefresh"

import ActivePrompt from "./ActivePrompt"
import NewPrompt from "./NewPrompt"
import PromptHistory from "./PromptHistory"
import { PromptWithResponses } from "./types"
import usePromptEvents from "./usePromptEvents"

type TeacherQuestionsPanelProps = {
  audioRoomId: string
  presentStudents: { fullName: string; id: string }[]
  active: boolean
}

const TeacherQuestionsPanel: React.FC<TeacherQuestionsPanelProps> = ({
  audioRoomId,
  presentStudents,
  active,
}) => {
  const [promptsResult, refetchPrompts] = useQuery({
    query: promptsQuery,
    variables: { audioRoomId },
  })
  const [prompts, setPrompts] = useState<PromptWithResponses[]>([])

  usePromptEvents(setPrompts)

  const activePeriod = promptsResult.data?.audioRoom?.activeAcrClassPeriod
  useClassPeriodRefresh(
    activePeriod?.endsAt,
    promptsResult.data?.audioRoom?.upcomingAcrClassPeriod?.startsAt,
    refetchPrompts
  )

  useEffect(() => {
    if (activePeriod?.prompts) setPrompts(activePeriod.prompts)
  }, [activePeriod])

  const activePrompt = prompts[0]?.closedAt ? undefined : prompts[0]
  const closedPrompts = prompts.filter(prompt => prompt.closedAt)

  return active ? (
    <WithLoadingIndicator result={promptsResult}>
      {!activePeriod ? (
        <p className="p-4 text-sm italic text-sky-200">
          Questions may only be sent while a class is in session.
        </p>
      ) : (
        <div className="flex h-full min-h-0 flex-col">
          <PromptHistory
            prompts={ordered(closedPrompts, "closedAt")}
            presentStudents={presentStudents}
          />

          <div className="flex-none border-t border-solid border-neutral-700 p-4">
            {activePrompt ? (
              <ActivePrompt
                prompt={activePrompt}
                presentStudents={presentStudents}
              />
            ) : (
              <NewPrompt audioRoomId={audioRoomId} />
            )}
          </div>
        </div>
      )}
    </WithLoadingIndicator>
  ) : null
}

const promptsQuery = gql`
  query TeacherAcrClassPrompts($audioRoomId: ID!) {
    audioRoom(id: $audioRoomId) {
      activeAcrClassPeriod {
        id
        endsAt
        prompts {
          id
          body
          answerType
          closedAt
          responses {
            id
            answer
            answeredAt
            student {
              id
              fullName
            }
          }
        }
      }
      upcomingAcrClassPeriod {
        startsAt
      }
    }
  }
`

export default TeacherQuestionsPanel
